import React, { useState, useEffect, Suspense } from 'react';
import { lazyRetry as lazy } from './utils/lazyRetry';
import { Capacitor } from '@capacitor/core';
import './App.css';
import { supabase } from './supabase';
import useStore from './store/useStore';
import { applyTheme } from './utils/helpers';
import { PALETTES } from './utils/constants';
import { PAGE_PERMS } from './utils/permissions';
import { loadUserData, resetLoadState, clearSessionData } from './services/loadUser';
import { clearResultsCache } from './pages/ParentPortal/resultsCache';
import { EdEaseLoader } from './components/brand/EdEaseLogo';

import Sidebar  from './components/layout/Sidebar';
import Topbar   from './components/layout/Topbar';

import Login      from './pages/Login';
import PasswordChangeGate from './components/PasswordChangeGate';

// Pages load on demand, so the first screen does not wait for every module in the app
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Students = lazy(() => import('./pages/Students'));
const Families = lazy(() => import('./pages/Families'));
const Attendance = lazy(() => import('./pages/Attendance'));
const Fees = lazy(() => import('./pages/Fees'));
const POS = lazy(() => import('./pages/POS/pos_index'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Exams = lazy(() => import('./pages/Exams'));
const Staff = lazy(() => import('./pages/Staff'));
const Reports = lazy(() => import('./pages/Reports'));
const Documents = lazy(() => import('./pages/Documents'));
const Timetable = lazy(() => import('./pages/Timetable'));
const Diary = lazy(() => import('./pages/Diary'));
const Settings = lazy(() => import('./pages/Settings'));
const ParentPortal = lazy(() => import('./pages/ParentPortal'));
const Complaints = lazy(() => import('./pages/Complaints'));
const StudentAffairs = lazy(() => import('./pages/StudentAffairs'));
const Finance = lazy(() => import('./pages/Finance'));
const PlatformSchools = lazy(() => import('./pages/Platform'));
const PlatformOverview = lazy(() => import('./pages/Platform/Overview'));
const PlatformPayments = lazy(() => import('./pages/Platform/Payments'));
const PlatformDemoAccess = lazy(() => import('./pages/Platform/DemoAccess'));
const PlatformLeads = lazy(() => import('./pages/Platform/Leads'));
const AdminMobile = lazy(() => import('./pages/AdminMobile'));
const TeacherMobile = lazy(() => import('./pages/TeacherMobile'));

// Signed in, but the role does not match any app layout. Nothing is shown and nothing is guessed.
function NoAppAccess() {
  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, textAlign: 'center', padding: 24, background: 'var(--bg)' }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>No access</div>
      <div style={{ fontSize: 13, color: 'var(--text-hint)', maxWidth: 320 }}>Your account is not set up for this app. Please contact your school administrator.</div>
      <button className="btn btn-secondary btn-sm" onClick={() => { clearSessionData(); supabase.auth.signOut(); }}>Sign out</button>
    </div>
  );
}

function NotAuthorized() {
  const { setPage } = useStore();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 12, textAlign: 'center' }}>
      <i className="ti ti-lock" style={{ fontSize: 48, color: 'var(--text-hint)', opacity: 0.35 }} aria-hidden="true" />
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Access Denied</div>
      <div style={{ fontSize: 13, color: 'var(--text-hint)' }}>You don't have access to this page.</div>
      <button className="btn btn-secondary btn-sm" onClick={() => setPage('dashboard')}>Back to Dashboard</button>
    </div>
  );
}

const IS_NATIVE = Capacitor.isNativePlatform();
const ADMIN_APP_ROLES = ['admin', 'owner', 'director', 'accountant', 'cashier'];

function AppInner() {
  const { user, setUser, page, setPage, students, settings, setSettings, dbLoading, setDbLoading, hasPermission } = useStore();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [bootGiveUp, setBootGiveUp] = useState(false);

  // ── Apply the saved theme on mount. Settings -> Display & Theme is the only theme source.
  useEffect(() => {
    if (settings.themeVars) applyTheme(settings.themeVars);
  }, []);

  // ── Auth listener
  useEffect(() => {
    let handledInitial = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // onAuthStateChange fires once immediately on mount with the current
      // session (event 'INITIAL_SESSION' or 'SIGNED_IN'), then again on real
      // changes. Calling loadUserData from BOTH this and a separate
      // getSession() call was the actual bug — always fired twice on every
      // load. This is now the ONLY place loadUserData gets called from
      // auth events (the superadmin school switcher in Topbar also calls it
      // directly, with force: true).
      if (session?.user) {
        loadUserData(session.user);
      } else {
        resetLoadState();
        clearResultsCache();   // saved results lists must not outlive the session
        clearSessionData();    // students, fees, accounts, products and the last page belong to the user who left
        setUser(null);
        setDbLoading(false);
      }
      handledInitial = true;
    });

    // Fallback only for the rare case onAuthStateChange doesn't fire at all
    // within a short window (some browser/storage edge cases) — but guarded
    // so it never runs if the listener above already handled it.
    const fallbackTimer = setTimeout(() => {
      if (!handledInitial) {
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session?.user) loadUserData(session.user);
          else setDbLoading(false);
        });
      }
    }, 1500);

    return () => {
      subscription.unsubscribe();
      clearTimeout(fallbackTimer);
    };
  }, []);

  // A time-limited (demo) login signs itself out the moment its access ends.
  useEffect(() => {
    if (!user?.accessExpiresAt) return undefined;
    const ms = new Date(user.accessExpiresAt).getTime() - Date.now();
    const endNow = () => {
      useStore.getState().setLoginNotice('Your access has ended. Please contact EdEase for a new login.');
      clearSessionData();
      supabase.auth.signOut();
    };
    if (ms <= 0) { endNow(); return undefined; }
    const t = setTimeout(endNow, Math.min(ms, 2147483000));
    return () => clearTimeout(t);
  }, [user?.accessExpiresAt]);

  // Safety valve: never trap the user on the boot loader if loading stalls
  useEffect(() => {
    const t = setTimeout(() => setBootGiveUp(true), 8000);
    return () => clearTimeout(t);
  }, []);

  // Hold a loader until the first session check finishes (avoids a login-page flash)
  if (!user && dbLoading && !bootGiveUp) {
    return (
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: IS_NATIVE ? '#1E2128' : 'oklch(0.95 0.025 285)' }}>
        <EdEaseLoader size={72} variant={IS_NATIVE ? 'white' : 'ink'} />
      </div>
    );
  }

  if (!user) return <Login />;
  if (user.passwordChanged === false) return <PasswordChangeGate user={user} />;
  if (user.roleName === 'parent' || user.roleName === 'student') return <ParentPortal />;
  // The Android app picks its layout from who the user is, not from one permission:
  // staff roles get AdminMobile, teachers get TeacherMobile, and nobody falls through to the desktop layout.
  if (IS_NATIVE) {
    if (user.isPlatformAdmin || ADMIN_APP_ROLES.includes(user.roleName)) return <AdminMobile />;
    if (user.staffId) return <TeacherMobile />;
    return <NoAppAccess />;
  }

  const renderPage = () => {
    // The platform panel is guarded by isPlatformAdmin, not a key.
    if (page.startsWith('platform')) {
      if (!user?.isPlatformAdmin) return <NotAuthorized />;
      if (page === 'platform-schools') return <PlatformSchools />;
      if (page === 'platform-onboard') return <PlatformSchools openWizard />;
      if (page === 'platform-payments') return <PlatformPayments />;
      if (page === 'platform-demo') return <PlatformDemoAccess />;
      if (page === 'platform-leads') return <PlatformLeads />;
      return <PlatformOverview />;
    }
    // Every other page needs its key. A page id that has no key is unknown and falls back to the dashboard.
    const perm = PAGE_PERMS[page] || PAGE_PERMS.dashboard;
    if (!hasPermission(perm)) return <NotAuthorized />;
    switch (page) {
      case 'dashboard':  return <Dashboard />;
      case 'students':   return <Students showAddModal={showAddStudent} onCloseAddModal={() => setShowAddStudent(false)} />;
      case 'families':   return <Families />;
      case 'attendance': return <Attendance />;
      case 'fees':       return <Fees />;
      case 'pos':        return <POS />;
      case 'inventory':  return <Inventory />;
      case 'exams':      return <Exams />;
      case 'staff':      return <Staff />;
      case 'reports':    return <Reports />;
      case 'documents':  return <Documents />;
      case 'timetable':  return <Timetable />;
      case 'diary':      return <Diary />;
      case 'complaints': return <Complaints />;
      case 'affairs':    return <StudentAffairs />;
      case 'finance':    return <Finance />;
      case 'settings':   return <Settings />;
      default:           return <Dashboard />;
    }
  };

  return (
    <div className="app-shell">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(v => !v)}
      />
      <div className="main-area">
        <Topbar
          onAddStudent={() => { setPage('students'); setShowAddStudent(true); }}
        />
        <main className="page-content">
          <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)' }}>Loading...</div>}>
            {renderPage()}
          </Suspense>
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Suspense fallback={<EdEaseLoader size={72} variant="ink" />}>
      <AppInner />
    </Suspense>
  );
}
